import { browserSessionPersistence, getAuth, sendPasswordResetEmail, signInWithEmailAndPassword } from "firebase/auth";
import { getFirestore, doc, getDoc, addDoc, collection, setDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
const auth = getAuth();
const db = getFirestore();
const functions = getFunctions();

async function logIn(username, password) {
  return auth.setPersistence(browserSessionPersistence).then(() => {
    return signInWithEmailAndPassword(auth, username, password);
  });
}
async function completeLogin() {
  return auth.currentUser;
}
const authModule = {
  namespaced: true,
  state: () => ({
    user: undefined,
    token: undefined,
    loginError: undefined,
    forgotError: undefined,
    loggedIn: false,
    success: false,
    card: {}
  }),
  getters: {
    getUser: (state) => {
      return state.user;
    },
    getToken: (state) => {
      return state.token;
    },
    getLoginError: (state) => {
      return state.loginError;
    },
    getForgotError: (state) => {
      return state.forgotError;
    },
    isDriver: (state) => {
      if (state.token) {
        return state.token.claims.driver;
      }
      return false;
    },
    isAdmin: (state) => {
      if (state.token)
        return state.token.claims.admin;
      return false;
    },
    isClient: (state) => {
      if (state.token)
        return state.token.claims.client;
      return false;
    },
    isLoggedIn: (state) => {
      return state.loggedIn;
    },
    getUserCard: (state) => {
      return state.card;
    }
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
    },
    setToken(state, token) {
      state.token = token;
    },
    setLoginSuccess(state) {
      state.loggedIn = true;
    },
    setLoginError(state, error) {
      state.loggedIn = false;
      state.loginError = error;
    },
    setForgotError(state, error) {
      state.forgotError = error;
    },
    logout(state) {
      state.user = undefined;
      state.token = undefined;
      state.loggedIn = false;
    },
    setCard(state, card){
      state.card = card;
    }
  },
  actions: {
    login(context, { username, password }) {
      logIn(username, password)
      .then(() => {
        console.log('logged in');
      })
        .catch((error) => {
          context.commit('setLoginError', error);
          console.log(error)
        });
    },
    async forgot(context, email){
      await sendPasswordResetEmail(auth, email)
      .then(() => {
        alert(`Password reset for ${email} has been confirmed.\nPlease check your email spam folder if you don't see the email in your inbox folder`);
      })
      .catch((err) => {
        alert(err);
        context.commit('setForgotError', err);
      });

    },
    completeLogin(context) {
      if(!context.getters.isLoggedIn){
        completeLogin().then(async (user) => {
          if (user) {
            const docRef = doc(db, 'users', user.uid);
            const docSnap = await getDoc(docRef);
            
            const token = user.getIdTokenResult();
  
            await Promise.all([docSnap, token])
              .then(([userMeta, tokenData]) => {
                const userData = userMeta.data();
                const loggedInUser = { ...userData, id: userMeta.id };
                context.commit('setToken', tokenData);
                context.commit('setUser', loggedInUser);
                context.commit('setLoginSuccess');
              });
          } else {
            context.commit('logout');
          }
        })   
      }
    },
    async registerUser(context, user){
      //using callable function to create user/customer
      const stripeCustomer = httpsCallable(functions, 'stripeCustomer');
      await stripeCustomer({
        id: user.id,
        emailAddress: user.emailAddress,
        companyName: user.companyName ? user.companyName : user.firstName + ' ' + user.lastName,
        mobile: user.mobile,
        firstName: user.firstName,
        lastName: user.lastName,
        vatNo: user.vatNo,
        scope: user.scope
      })
      .then(() => {
        getDoc(doc(db, 'users', user.id))
        .then((snapshot) =>{
          context.commit('setUser', snapshot.data());
        })
      })
      .catch((error) => {
        console.log(error);
        alert('Something went wrong');
      });

      //sends sms to admin if registering customer is a driver pending
      if (user.scope == 'pending'){
        let userData = {
          name: user.companyName ? user.companyName : user.firstName + " " + user.lastName,
          username: user.firstName + " " + user.lastName,
          email: user.emailAddress,
          mobile: user.mobile,
          scope: user.scope
        };
        let requestOptions = {
          method: 'POST',
          headers: [
            ['Content-type', 'application/json']
          ],
          body: JSON.stringify(userData),
          redirect: 'follow'
        }
        fetch("https://us-central1-wrapp-waste.cloudfunctions.net/sendRegSms", requestOptions)
        .catch((error) => {
          console.log(error);
        })
      }
    },
    async registerUserCard(_context, user){
      const paymentMethod = httpsCallable(functions, 'paymentMethod');
      await paymentMethod({
        id: user.id,
        token: user.token
      })
      .then(() => {
        alert('Successfully added card');
        getDoc(doc(db, 'users', user.id))
        .then(async (snapshot) =>{
          if(user.registration){
            _context.commit('setUser', snapshot.data());
            //complete or finish registration for card users here
            await setDoc(doc(db, 'users', user.id), {
              registered: true
            }, { merge: true })
            .then(async () => {
              //clear user login session
              await auth.signOut()
              .then(() =>{
                sessionStorage.clear();
                _context.commit('auth/logout');
              });
              alert('Your account has been successfully created.\nPlease Log in');
              window.location.href = '/login';
            })
            .catch((error) => console.log(error))
          }
          else{
            await _context.commit('setUser', snapshot.data());
            window.location.reload();
          }   
        }) 
      })
      .catch(() => {
        alert('Failed to add card');
        _context.commit('isSuccess', false);
      })
    },
    async completeRegistration(_context, user){
      await setDoc(doc(db, 'users', user.id), {
        registered: true
      }, { merge: true })
      .then(async () => {
        //clear user login session
        await auth.signOut()
          .then(() =>{
            sessionStorage.clear();
            _context.commit('auth/logout');
          });
          alert('Your account has been successfully created.\nPlease Log in');
          window.location.href = '/login';
      })
      .catch((error) => console.log(error))
    },
    async fetchUserCard(_context, user){
      const viewCard = httpsCallable(functions, 'viewCard');
      await viewCard({
        stripeId: user.stripeId,
        paymentMethod: user.paymentMethod
      })
      .then((userCard) => _context.commit('setCard', userCard))
      .catch((error) => {
        alert('Failed to load card details');
        console.log(error);
      })
    },
    async addBookingQuote(_context, booking){
      const quoteSnapshot = await getDoc(doc(db, 'quotes', booking.id));
      const quote = quoteSnapshot.data();
      // Save booking from fast quote
      await addDoc(collection(db, 'bookings'), {
        ...quote,
        userId: booking.userId
      })
      .catch((err) =>{
        console.log(err);
      })

    }
  }
}

export default authModule;